import type { GenerationMetaSnapshot } from '@/lib/api/quality-types.js';
type Context = NonNullable<
  GenerationMetaSnapshot['frozenInput']
>['knowledgeContext'];
const reasons: Record<string, string> = {
  sufficient_context: 'Enough context for the selected scope.',
  no_new_context: 'A different search added no new evidence.',
  limit_reached: 'Search budget reached.',
  source_error: 'Search ended with a source error.',
  not_used: 'Knowledge was not searched.',
  worker_stopped:
    'The worker finished without an explicit context-stop reason.',
  cancelled: 'Search was cancelled.',
};
export function KnowledgeSearchSummary({ context }: { context?: Context }) {
  const accounting = context?.accounting;
  if (!context?.enabled || !accounting) return null;
  const early =
    ['sufficient_context', 'no_new_context'].includes(
      context.stop_reason ?? '',
    ) && accounting.logicalRequests < accounting.maxRequests;
  return (
    <div
      className="my-3 space-y-1 rounded border px-3 py-2 text-xs"
      aria-label="Knowledge search accounting"
    >
      <p>
        {accounting.logicalRequests}/{accounting.maxRequests} search requests ·{' '}
        {accounting.chunksPerRequest} chunks per request ·{' '}
        {accounting.transportAttempts} transport attempts
      </p>
      <p>
        {accounting.receivedChunks} observed received chunks ·{' '}
        {accounting.retainedChunks} retained · {accounting.omittedChunks}{' '}
        omitted{accounting.complete ? '' : ' · counters incomplete'}
      </p>
      <p>
        {accounting.deliveredChunks === undefined ||
        accounting.deliveredChunks === null
          ? 'Delivery count unavailable'
          : `${accounting.deliveredChunks} chunks confirmed delivered`}
        {accounting.deliveryComplete === false
          ? ' · delivery evidence incomplete'
          : ''}
      </p>
      {context.stop_reason ? (
        <p
          role={early ? 'status' : undefined}
          className={early ? 'text-warning-600' : 'text-muted-foreground'}
        >
          {early ? 'Search stopped before the budget: ' : ''}
          {reasons[context.stop_reason] ?? 'Stop reason unavailable.'}
          {context.frozen
            ? ' Evidence is frozen for this generation version.'
            : ''}
        </p>
      ) : null}
    </div>
  );
}
